import { Badge, Col, Row, Stack } from 'react-bootstrap';
import { IoHourglassOutline } from 'react-icons/io5';
import { HiOutlineShare } from 'react-icons/hi';
import { LuCalendarDays } from 'react-icons/lu';
import { FiPlusCircle } from 'react-icons/fi';
import { GoPerson } from 'react-icons/go';
import ViewOffSlashIcon from '@components/Icons/ViewOffSlash/ViewOffSlash';
import TwoPersonIcon from '@components/Icons/TwoPerson/TwoPerson';
import IJobDetail, { JobMember } from '@th-types/job.detail.type';
import { IJobStats } from '@th-types/jobs.type';
import DATE_FORMATS from '@constants/dateFormat';
import { formatLocalDate, UTC_TIMEZONE } from '@utils/DateUtils';
import {
  SMALL_ICONS_SIZE,
  getAddressLink,
  getFilledPosition,
  getTotalHiddenApplicants,
} from '@utils/JobUtils';
import './style.scss';
import { Link } from 'react-router-dom';
import { BASE_PATH } from '@utils/Environment';

interface Props {
  job: IJobDetail;
  jobStats?: IJobStats;
  showJobMembers: (members: JobMember[]) => void;
  handleOpenModalShareJobLink: (job: IJobDetail) => void;
}

export default function JobShiftsItemHeader({
  job,
  jobStats,
  showJobMembers,
  handleOpenModalShareJobLink,
}: Props) {
  if (!jobStats) {
    return null;
  }

  return (
    <header>
      <Row className="px-2">
        <Col md={8} className="job-shifts-title">
          <Link
            className="item-links fs-6 text-capitalize fw-bold"
            target="_blank"
            to={`${BASE_PATH}jobs/${job.id}`}
          >
            {job.eventName}
            {!job.datePublished && (
              <Badge className="ms-2" bg="secondary" text="dark">
                Draft
              </Badge>
            )}
          </Link>
        </Col>
        <Col md={4} className="d-flex justify-content-end">
          <Link
            className="item-links text-capitalize add-shifts pt-1"
            target="_blank"
            to={`${BASE_PATH}jobs/${job.id}/scheduling`}
          >
            <FiPlusCircle
              size={SMALL_ICONS_SIZE}
              className="mb-1 me-1"
              data-testid="add-shifts-icon"
            />
            <strong>ADD SHIFTS</strong>
          </Link>
        </Col>
      </Row>
      <Row className="ps-2 job-shifts-subheading">
        <Col>
          {job.title.name} at <strong>{job.brandRepresented}</strong>
        </Col>
      </Row>
      <Row className="ps-2">
        <div className="item-info">
          {getAddressLink(job.nationalJob, job.address)}
        </div>
      </Row>
      <Row className="ps-2 me-2 pb-1">
        <div className="d-flex justify-content-between w-100 pe-0">
          <Stack direction="horizontal" gap={4}>
            <div>
              <LuCalendarDays size={SMALL_ICONS_SIZE} className="mb-1 me-1" />
              {formatLocalDate(
                job.localizedStart,
                DATE_FORMATS.DATE_FORMAT_SHORT,
                UTC_TIMEZONE
              )}{' '}
              -{' '}
              {job.localizedEnd
                ? formatLocalDate(
                    job.localizedEnd,
                    DATE_FORMATS.DATE_FORMAT_SHORT,
                    UTC_TIMEZONE
                  )
                : 'on going'}
            </div>
            <div
              data-testid="show-job-member-id"
              onClick={() => {
                const jobMembers = job.members?.filter(
                  (item) => item.isInternal
                );
                if (jobMembers) {
                  showJobMembers(jobMembers);
                }
              }}
              className={`${job.members?.length > 0 ? 'cursor-point' : ''}`}
            >
              <GoPerson size={SMALL_ICONS_SIZE} className="mb-1 me-1" />
              <strong className="text-decoration-underline">
                Internal:{' '}
                {job.members?.filter((item) => item.isInternal).length || 0}
              </strong>
            </div>
            <div>
              <TwoPersonIcon size={SMALL_ICONS_SIZE} className="mb-1" />
              <span className="ps-1">
                Hired: <strong>{getFilledPosition(job, jobStats)}</strong>
              </span>
            </div>

            <div>
              <IoHourglassOutline size={SMALL_ICONS_SIZE} className="mb-1" />
              <span className="ps-1">
                Pending: <strong>{jobStats.countPendingWorkers}</strong>
              </span>
            </div>

            <div>
              <ViewOffSlashIcon size={SMALL_ICONS_SIZE} className="mb-1" />
              <span className="ps-1">
                New Applicants: <strong>{jobStats.countNewApplicants}</strong>
              </span>
            </div>
            <div>
              <ViewOffSlashIcon size={SMALL_ICONS_SIZE} className="mb-1" />
              <span className="ps-1">Hidden: </span>
              <b>{getTotalHiddenApplicants(jobStats)}</b>
            </div>
          </Stack>

          <Stack
            direction="horizontal"
            gap={3}
            className="d-flex justify-content-end"
          >
            <HiOutlineShare
              size={22}
              className="cursor-point icons"
              onClick={() => handleOpenModalShareJobLink(job)}
              data-testid="share-icon"
            />
          </Stack>
        </div>
      </Row>
    </header>
  );
}
